/**
 * NP-484 — on a real mobile device, locking the screen for 30+ minutes and
 * reopening Path left the user signed in.
 *
 * SessionTimeout drove everything from `setTimeout` / `setInterval`. Those are
 * throttled in a backgrounded tab and stop entirely while an iOS device is
 * locked, so the 25-minute idle timer simply never fired: no warning, no
 * countdown, no logout. Worse, mobile browsers evict backgrounded tabs and
 * reload the document on return, which reset the in-memory timer and handed the
 * user a *fresh* 30 minutes.
 *
 * A timer can only measure time the CPU was awake. Idle-out has to be decided
 * from the wall clock, against a timestamp that outlives the document — hence
 * this module: one persisted `lastActivityAt`, and one pure function that turns
 * it plus "now" into a decision. Pure because the thing that actually broke is
 * arithmetic across a suspend, and arithmetic is testable; a locked phone is not.
 *
 * NOT to be confused with `path_lastActivityDate`, which belongs to
 * useInactivityArchival and tracks 30-DAY loan archival. Different clock,
 * different feature, deliberately a different key.
 */

/** Idle time before the warning dialog appears. */
export const IDLE_TIMEOUT_MS = 25 * 60 * 1000;

/** Countdown length once the warning is up. 25 + 5 = the 30 minutes QA tests. */
export const WARNING_DURATION_MS = 5 * 60 * 1000;

/** Total idle time before the session is over. */
export const SESSION_IDLE_LIMIT_MS = IDLE_TIMEOUT_MS + WARNING_DURATION_MS;

const STORAGE_KEY = 'path_session_last_activity';

export type IdleState =
  | { phase: 'active'; msUntilWarning: number }
  | { phase: 'warning'; secondsLeft: number }
  | { phase: 'expired' };

/**
 * Where the session stands, given when the user was last active and what time it
 * is now. Total function: any stored value, any clock, yields a phase.
 *
 * `lastActivityAt` of null means "nothing recorded" — treated as ACTIVE with a
 * full window rather than expired, so a first-ever page load cannot bounce a
 * freshly authenticated user straight back to /login.
 */
export function evaluateIdleState(lastActivityAt: number | null, now: number): IdleState {
  // Review (NP-484): the docstring promises a total function, so honour it for
  // non-finite input too. NaN/Infinity would otherwise fall straight through the
  // comparisons below and emit `msUntilWarning: NaN`, which becomes a setTimeout
  // delay of NaN — i.e. fires immediately. Treated as "nothing recorded".
  if (lastActivityAt === null || !Number.isFinite(lastActivityAt) || !Number.isFinite(now)) {
    return { phase: 'active', msUntilWarning: IDLE_TIMEOUT_MS };
  }

  // A timestamp in the future means the clock moved backwards (travel, DST, a
  // manual change). Treat it as "just active" rather than expiring someone for
  // something their device did.
  const idleFor = Math.max(0, now - lastActivityAt);

  if (idleFor >= SESSION_IDLE_LIMIT_MS) {
    return { phase: 'expired' };
  }

  if (idleFor >= IDLE_TIMEOUT_MS) {
    // Mid-countdown: resume with the time that is genuinely left, not a fresh
    // five minutes. Ceil so a user never sees 0:00 on a live session.
    const secondsLeft = Math.ceil((SESSION_IDLE_LIMIT_MS - idleFor) / 1000);
    return { phase: 'warning', secondsLeft };
  }

  return { phase: 'active', msUntilWarning: IDLE_TIMEOUT_MS - idleFor };
}

/** Reads the persisted activity stamp, or null when absent/unusable. */
export function readLastActivityAt(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    // Review (NP-484): `!raw` is not enough. `Number(' ') === 0` and 0 is finite,
    // so a whitespace-corrupted value read as epoch-0 — an idle time of decades,
    // which signed the user out on the spot. Trim first.
    if (!raw || !raw.trim()) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // Private browsing / storage disabled — fall back to timer-only behaviour
    // rather than throwing inside an event handler.
    return null;
  }
}

export function writeLastActivityAt(at: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(at));
  } catch {
    /* storage unavailable — see readLastActivityAt */
  }
}

export function clearLastActivityAt(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable — see readLastActivityAt */
  }
}
